import React, { FC, useRef, useState } from "react";
import dayjs from "dayjs";

import { useOutsideClick } from "@hooks";
import { useI18n } from "@i18n";
import { Nullable } from "@utils";
import {
  Box,
  ClearButton,
  Container,
  DatesBlock,
  Month,
  MonthsWrapper,
  Popup,
  SvgButton,
  Trigger,
  Wrapper,
  Year,
  YearSelector,
} from "./styles";

interface IMonthPicker {
  initialFrom: Nullable<Date>;
  initialTo: Nullable<Date>;
  setMonths: (from: Nullable<Date>, to: Nullable<Date>) => void;
  trigger?: string | JSX.Element;
  isClearing?: boolean;
  showDates?: boolean;
}

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const today = new Date();

export const MonthPicker: FC<IMonthPicker> = ({
  initialFrom = null,
  initialTo = null,
  setMonths,
  trigger = "Select months",
  showDates = false,
}) => {
  const { t } = useI18n();

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const defaultRightDate = dayjs(initialTo ?? today)
    .startOf("year")
    .toDate();

  const defaultLeftDate =
    initialFrom &&
    initialTo &&
    initialFrom.getFullYear() === initialTo.getFullYear()
      ? dayjs(initialFrom).startOf("year").subtract(1, "year").toDate()
      : initialFrom
      ? dayjs(initialFrom).startOf("year").toDate()
      : dayjs(today).startOf("year").subtract(1, "year").toDate();

  const [leftDate, setLeftDate] = useState(defaultLeftDate);
  const [rightDate, setRightDate] = useState(defaultRightDate);

  const [startDate, setStartDate] = useState<Nullable<Date>>(initialFrom);
  const [endDate, setEndDate] = useState<Nullable<Date>>(initialTo);
  const [hoverDate, setHoverDate] = useState<Nullable<Date>>(null);

  const handleMonthClick = (idx: number, current: Date) => {
    const newDateJs = dayjs(current).add(idx, "month");
    const newDate = newDateJs.toDate();
    if ((!startDate && !endDate) || (startDate && endDate)) {
      setStartDate(newDate);
      setEndDate(null);
    } else {
      // User selected start and end
      let start: Nullable<Date>, end: Nullable<Date>;
      if (startDate && newDateJs.isBefore(startDate)) {
        start = newDate;
        end = dayjs(startDate).endOf("month").toDate();
        setEndDate(end);
        setStartDate(newDate);
      } else {
        start = startDate;
        end = newDateJs.endOf("month").toDate();
        setEndDate(end);
      }
      handleApply(start, end);
    }
  };

  const handleClose = () => {
    setLeftDate(defaultLeftDate);
    setRightDate(defaultRightDate);
    setStartDate(initialFrom);
    setEndDate(initialTo);
    setHoverDate(null);
    setOpen(false);
  };

  const handleApply = (start: Nullable<Date>, end: Nullable<Date>) => {
    setMonths(start, end);
    setHoverDate(null);
    setOpen(false);
  };

  const handleClear = () => {
    setLeftDate(defaultLeftDate);
    setRightDate(defaultRightDate);
    setStartDate(null);
    setEndDate(null);
    setMonths(null, null);
    setHoverDate(null);
    setOpen(false);
  };

  useOutsideClick(wrapperRef, handleClose);

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger onClick={() => setOpen(!open)}>{trigger}</Trigger>
      {open && (
        <Popup>
          {showDates ? (
            <DatesBlock>
              <div>Select a range of months</div>
              {startDate || endDate ? (
                <ClearButton onClick={handleClear}>
                  {t("filters.clear")}
                </ClearButton>
              ) : null}
            </DatesBlock>
          ) : null}
          <Container>
            <Box>
              <YearSelector>
                <SvgButton
                  icon="LeftArrow"
                  rounded
                  bordered
                  onClick={() =>
                    setLeftDate((prev) =>
                      dayjs(prev).subtract(1, "year").toDate()
                    )
                  }
                />
                <Year>
                  <b>{leftDate.getFullYear()}</b>
                </Year>
                <SvgButton
                  icon="RightArrow"
                  rounded
                  bordered
                  onClick={() => {
                    const newLeftDate = dayjs(leftDate).add(1, "year").toDate();
                    setLeftDate(newLeftDate);
                    if (rightDate.getFullYear() === newLeftDate.getFullYear())
                      setRightDate(dayjs(newLeftDate).add(1, "year").toDate());
                  }}
                />
              </YearSelector>
              <MonthsWrapper>
                {months.map((month, idx) => {
                  const selectedMonth = startDate?.getMonth();
                  const currentMonth = dayjs(leftDate)
                    .add(idx, "month")
                    .toDate();
                  const highlight =
                    (endDate && currentMonth) || (hoverDate && startDate);
                  const isActive =
                    (selectedMonth === idx &&
                      startDate?.getFullYear() === leftDate.getFullYear()) ||
                    (highlight &&
                      dayjs(currentMonth).isBetween(
                        startDate ?? today,
                        (endDate || hoverDate) ?? today,
                        "day",
                        "[]"
                      ));

                  return (
                    <Month
                      active={isActive}
                      key={idx}
                      onClick={() => handleMonthClick(idx, leftDate)}
                      onMouseEnter={() => setHoverDate(currentMonth)}
                    >
                      {month}
                    </Month>
                  );
                })}
              </MonthsWrapper>
            </Box>
            <Box>
              <YearSelector>
                <SvgButton
                  icon="LeftArrow"
                  rounded
                  bordered
                  onClick={() => {
                    const newRightDate = dayjs(rightDate)
                      .subtract(1, "year")
                      .toDate();
                    setRightDate(newRightDate);
                    if (leftDate.getFullYear() === newRightDate.getFullYear())
                      setLeftDate(
                        dayjs(newRightDate).subtract(1, "year").toDate()
                      );
                  }}
                />
                <Year>
                  <b>{rightDate.getFullYear()}</b>
                </Year>
                <SvgButton
                  icon="RightArrow"
                  rounded
                  bordered
                  onClick={() =>
                    setRightDate((prev) => dayjs(prev).add(1, "year").toDate())
                  }
                />
              </YearSelector>
              <MonthsWrapper>
                {months.map((month, idx) => {
                  const selectedMonth = startDate?.getMonth();
                  const currentMonth = dayjs(rightDate)
                    .add(idx, "month")
                    .toDate();
                  const highlight =
                    (endDate && currentMonth) || (hoverDate && startDate);
                  const isActive =
                    (selectedMonth === idx &&
                      startDate?.getFullYear() === rightDate.getFullYear()) ||
                    (highlight &&
                      dayjs(currentMonth).isBetween(
                        startDate ?? today,
                        (endDate || hoverDate) ?? today,
                        "day",
                        "[]"
                      ));

                  return (
                    <Month
                      active={isActive}
                      key={idx}
                      onClick={() => handleMonthClick(idx, rightDate)}
                      onMouseEnter={() => setHoverDate(currentMonth)}
                    >
                      {month}
                    </Month>
                  );
                })}
              </MonthsWrapper>
            </Box>
          </Container>
        </Popup>
      )}
    </Wrapper>
  );
};

export default MonthPicker;
